/**
 * Definitions for resource and service & optional parameters.
 *
 * See: https://www.typescriptlang.org/docs/handbook/advanced-types.html
 */

import { Injectable, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';

type Resource =
  | 'agreements'
  | 'conversations'
  | 'messages'
  | 'timesheets'
  | 'posted_jobs'
  | 'consumers'
  | 'profiles'
  | 'search'
  | 'search_name'
  | 'settings'
  | 'bugs'
  | 'shortlist'
  | 'flex_jobs'
  | 'job_notification_filters'
  | 'dashboard'
  | 'invitations'
  | 'meet_and_greets'
  | 'availability'
  | 'shift_notes'
  | 'contacts';

interface ServiceParams {
  acctScoped?: boolean;
  namespace?: string;
  resource: Resource;
  parentResource?: Resource;
  parentResourceId?: number;
  paginated?: boolean;
  apiVersion?: string;
}

interface OptionalParams {
  [index: string]: any;
}

/**
 * Base template for resource services.
 */
@Injectable({
  providedIn: 'root',
})
export abstract class BaseService implements OnDestroy {
  /** Class members */
  // Generic counters to keep track of things (like current page)
  private counters: { [index: string]: number } = {};

  // Subject for automatic subscription cleanup: https://goo.gl/bzFQT4
  protected unsubscribe: Subject<void> = new Subject();

  // Subject for notification. Pages will subscribe to this so they
  // know when a notification occurs and update page accordingly.
  // Notification will be emitted from notification providers
  // eg notification-agreement.ts, notification-conversations.ts
  public notification: Subject<any> = new Subject();

  /**
   * Service attributes:
   *
   *  - Account scoped: if service is scoped by the user's account type
   *  - Namespace (for example: 'workers', 'api', etc)
   *  - Top-level or nested resources
   *  - Endpoint may accept optional parameters
   *  - Paginated by default with 20 entries per page
   */

  public acctScoped: boolean;

  private namespace: string;

  private resource: Resource;

  private parentResource: Resource;

  protected parentResourceId: number;

  protected optionalParams: OptionalParams = null;

  public paginated = true;

  public apiVersion = '';

  public pageSize = 20; // Customize on children services as needed

  // Default meta property. Only returned by v1 APIs
  public meta;

  public nextPageAvailable = true; // Backend response tells if there's a next page

  public showCleanSlate = false;

  public retrieving = false;

  constructor(params: ServiceParams) {
    this.acctScoped = params.acctScoped;
    this.namespace = params.namespace;
    this.resource = params.resource;
    this.parentResource = params.parentResource;
    this.parentResourceId = params.parentResourceId;
    this.paginated = params.paginated;
    this.apiVersion = params.apiVersion;
    if (this.paginated) {
      this.resetPage();
    }
  }

  // Public string representation
  resourceRepresentation(): string {
    return this.namespaceString() + this.parentResourceString() + this.resource;
  }

  getParams(): OptionalParams {
    return this.optionalParams;
  }

  setParams(params: OptionalParams) {
    this.optionalParams = params;
  }

  /* retrieving indication to use for loading on pages */

  protected retrieveStarted() {
    this.retrieving = true;
  }

  protected retrieveStopped() {
    this.retrieving = false;
  }

  /** Base pagination methods shared by all paginated services */

  nextPage(): Promise<void> {
    return Promise.resolve(); // Customize on paginated services as needed
  }

  protected incrementPage() {
    this.incrementCounter('page');
  }

  protected resetPage() {
    this.setCounter('page', 1);
    this.nextPageAvailable = null; // Backend response tells if there's a next page
  }

  /** Generic counters, e.g. to keep the current page for paginated resources */

  protected setCounter(idx = 'default', count = 1): number {
    this.counters[idx] = count;
    return this.counters[idx];
  }

  public getCounter(idx = 'default'): number {
    if (typeof this.counters[idx] !== 'number') {
      this.setCounter(idx);
    }
    return this.counters[idx];
  }

  protected incrementCounter(idx = 'default'): number {
    return this.setCounter(idx, this.getCounter(idx) + 1);
  }

  protected decrementCounter(idx = 'default'): number {
    return this.setCounter(idx, this.getCounter(idx) - 1);
  }

  /** Private utility members */

  private namespaceString(): string {
    return this.namespace ? `${this.namespace}/` : '';
  }

  private parentResourceString(): string {
    return !this.parentResource ? '' : `${this.parentResource}/${this.parentResourceId}/`;
  }

  getCopyOf(json: any) {
    return JSON.parse(JSON.stringify(json));
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }
}
